/**
 * What the run actually did: models, prompt versions, tokens, cost and time per step.
 *
 * This is the operations view, deliberately last. An analyst does not need it to read a legajo,
 * but when something goes wrong it is the only screen that answers "what happened".
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, friendlyError } from "../../api/client";
import { STEP_LABELS, STEP_ORDER } from "../../lib/labels";
import {
  diffHeadline,
  diffRows,
  isRunning,
  runTotals,
  stepTimeline,
  traceRows,
} from "../../lib/model";
import type { CaseOut } from "../../api/types";
import {
  Button,
  Chip,
  Dot,
  EmptyState,
  ErrorBanner,
  SectionTitle,
  Spinner,
  Stats,
  Table,
  Td,
  Th,
} from "../../components/ui";

const STEP_TONE = {
  done: "good",
  running: "warn",
  failed: "bad",
  skipped: "good",
  pending: "neutral",
} as const;

export default function Trace({
  kase,
  onResume,
}: {
  kase: CaseOut;
  onResume: (step: string) => void;
}) {
  const [step, setStep] = useState<string>(STEP_ORDER[0]);
  const runId = kase.latest_run?.id;

  const run = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.getRun(runId!),
    enabled: !!runId,
    refetchInterval: isRunning(kase) ? 2500 : false,
  });

  if (!runId) return <EmptyState title="Todavía no se ejecutó ningún análisis" />;
  if (run.isError) return <ErrorBanner {...friendlyError(run.error)} />;
  if (!run.data) return <Spinner />;
  const data = run.data;

  return (
    <>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div>
          <h2 className="text-base font-semibold">Progreso</h2>
          <ul className="mt-3 space-y-1.5">
            {stepTimeline(data).map((item) => (
              <li key={item.step} className="flex items-center gap-2.5 text-sm">
                <Dot tone={STEP_TONE[item.status as keyof typeof STEP_TONE] ?? "neutral"} />
                <span className={item.status === "pending" ? "text-muted" : ""}>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-base font-semibold">Consumo</h2>
          <div className="mt-3">
            <Stats items={runTotals(data)} />
          </div>
          <p className="mt-3 text-sm text-muted">
            Se registra por corrida: modelo, versión de prompt, tokens, costo y duración de cada
            paso.
          </p>
        </div>
      </div>

      <SectionTitle>Detalle por paso</SectionTitle>
      <Table>
        <thead>
          <tr>
            <Th>Paso</Th>
            <Th right>Intento</Th>
            <Th>Estado</Th>
            <Th>Modelo</Th>
            <Th right>Tokens</Th>
            <Th right>Costo USD</Th>
            <Th right>Duración</Th>
            <Th>Error</Th>
          </tr>
        </thead>
        <tbody>
          {traceRows(data).map((row, index) => (
            <tr key={index}>
              <Td className="font-medium">{row.name}</Td>
              <Td right className="text-muted">
                {row.attempt}
              </Td>
              <Td>
                <span className="flex items-center gap-2">
                  <Dot tone={row.tone} />
                  {row.status}
                </span>
              </Td>
              <Td className="text-muted">{row.model}</Td>
              <Td right className="text-muted">
                {row.tokens}
              </Td>
              <Td right className="text-muted">
                {row.cost}
              </Td>
              <Td right className="text-muted">
                {row.duration}
              </Td>
              <Td className="max-w-md text-xs break-words text-bad">{row.error}</Td>
            </tr>
          ))}
        </tbody>
      </Table>

      <RunComparison caseId={kase.id} />

      <details className="mt-8 max-w-2xl rounded-lg border border-line p-4">
        <summary className="cursor-pointer text-sm font-medium">
          Reanudar desde un paso específico
        </summary>
        <p className="mt-2 text-sm text-muted">
          Normalmente no hace falta: «Reintentar análisis» ya reanuda desde donde falló. Esto es
          para volver a correr un paso puntual, por ejemplo tras cambiar un prompt.
        </p>
        <div className="mt-3 flex gap-2">
          <select
            value={step}
            onChange={(e) => setStep(e.target.value)}
            className="rounded-lg border border-line px-3 py-2 text-sm"
          >
            {STEP_ORDER.map((name) => (
              <option key={name} value={name}>
                {STEP_LABELS[name]}
              </option>
            ))}
          </select>
          <Button onClick={() => onResume(step)}>Reanudar desde este paso</Button>
        </div>
      </details>
    </>
  );
}

/**
 * Qué cambió respecto de la corrida anterior.
 *
 * Un legajo se vuelve a analizar cuando el cliente trae lo que faltaba, y hasta acá el resultado
 * nuevo pisaba al anterior sin decir qué se movió: el analista que ya lo había revisado tenía que
 * volver a leerlo entero para descubrir que lo único distinto es el EBITDA — o peor, no lo revisaba
 * y se perdía que la calificación bajó dos grados.
 */
function RunComparison({ caseId }: { caseId: string }) {
  const diff = useQuery({
    queryKey: ["diff", caseId],
    queryFn: () => api.getDiff(caseId),
    networkMode: "always",
  });

  if (!diff.data) return null;
  const data = diff.data;
  if (data.reason) return null; // una sola corrida: no hay nada que comparar, y no es un error

  const headline = diffHeadline(data);
  const rows = diffRows(data);

  return (
    <>
      <SectionTitle>Cambios respecto de la corrida anterior</SectionTitle>
      {headline && (
        <p className="mb-3 text-sm">
          <Chip text={headline.text} tone={headline.tone} />
        </p>
      )}
      {rows.length === 0 ?
        <p className="text-sm text-muted">Ninguna cifra del perfil se movió.</p>
      : <Table>
          <thead>
            <tr>
              <Th>Campo</Th>
              <Th>Período</Th>
              <Th right>Antes</Th>
              <Th right>Ahora</Th>
              <Th>Qué pasó</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <Td className="font-medium">{row.label}</Td>
                <Td className="text-muted">{row.period}</Td>
                <Td right className="text-muted">
                  {row.before}
                </Td>
                <Td right>{row.after}</Td>
                <Td>
                  <span className="flex items-center gap-2">
                    <Dot tone={row.tone} />
                    {row.kind}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      }
      {(data.findings_added.length > 0 || data.findings_removed.length > 0) && (
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <FindingList title="Hallazgos nuevos" items={data.findings_added} tone="warn" />
          <FindingList
            title="Hallazgos que ya no aparecen"
            items={data.findings_removed}
            tone="good"
          />
        </div>
      )}
    </>
  );
}

function FindingList({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: "warn" | "good";
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="mt-2 space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm">
            <span className="mt-1.5">
              <Dot tone={tone} />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
